import { rolesControllerFindAll } from '@kalba/api-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Lock, Plus, Search, Settings2, Shield, Sparkles, Users, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../../components/layout/app-layout';
import { PageHeader } from '../../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../../components/shared/card-data-table';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { StatCard } from '../../../components/shared/stat-card';
import { Button } from '../../../components/ui/button';
import { ProgressBar } from '../../../components/ui/progress-bar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RoleListItem {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  createdAt: string;
  modules: string[];
  licensingAuthorities?: string[];
  _count: { permissions: number; users: number };
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/users/roles/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('role:view')) throw redirect({ to: '/dashboard' });
  },
  component: RolesPage,
});

// ── Module tag config ─────────────────────────────────────────────────────────

const MODULE_COLOR: Record<string, string> = {
  customer: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  permit: 'border-blue-200 bg-blue-50 text-blue-700',
  payment: 'border-sky-200 bg-sky-50 text-sky-700',
  notification: 'border-orange-200 bg-orange-50 text-orange-700',
  user: 'border-violet-200 bg-violet-50 text-violet-700',
  role: 'border-indigo-200 bg-indigo-50 text-indigo-700',
};

const MODULE_LABEL: Record<string, string> = {
  customer: 'Customers',
  permit: 'Permits',
  payment: 'Payments',
  notification: 'Notifications',
  user: 'Users',
  role: 'Roles',
};

function ModuleTag({ resource }: { resource: string }) {
  const cls = MODULE_COLOR[resource] ?? 'border-gray-200 bg-gray-100 text-gray-600';
  const label = MODULE_LABEL[resource] ?? resource.charAt(0).toUpperCase() + resource.slice(1);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        cls,
      )}
    >
      {label}
    </span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

// ── Sort options ──────────────────────────────────────────────────────────────

type SortKey = 'name-asc' | 'name-desc' | 'perms-desc' | 'users-desc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'perms-desc', label: 'Most Permissions' },
  { value: 'users-desc', label: 'Most Users' },
];

function sortRoles(roles: RoleListItem[], key: SortKey): RoleListItem[] {
  return [...roles].sort((a, b) => {
    if (key === 'name-asc') return a.name.localeCompare(b.name);
    if (key === 'name-desc') return b.name.localeCompare(a.name);
    if (key === 'perms-desc') return b._count.permissions - a._count.permissions;
    if (key === 'users-desc') return b._count.users - a._count.users;
    return 0;
  });
}

// ── List columns ──────────────────────────────────────────────────────────────

const ROLE_COLUMNS: CardDataTableColumn[] = [
  { id: 'role', header: 'Role' },
  { id: 'permissions', header: 'Permissions' },
  { id: 'users', header: 'Users' },
  { id: 'type', header: 'Type' },
];

const ROLE_GRID = 'minmax(0, 2fr) minmax(0, 1fr) 120px 110px';

// ── Authority filter options ──────────────────────────────────────────────────

const AUTHORITY_OPTIONS = [
  { value: '__all__', label: 'All Authorities' },
  { value: 'KALBA_MUNICIPALITY', label: 'Kalba Municipality' },
  { value: 'SHARJAH_MUNICIPALITY', label: 'Sharjah Municipality' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

function RolesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const licensingAuthorities = useAuthStore((s) => s.licensingAuthorities);
  const showAuthorityFilter = licensingAuthorities.length !== 1;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebounced] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'system' | 'custom'>('all');
  const [authorityFilter, setAuthorityFilter] = useState('__all__');
  const [sort, setSort] = useState<SortKey>('name-asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, authorityFilter, sort]);

  const {
    data: rawRoles,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesControllerFindAll() as unknown as Promise<RoleListItem[]>,
  });

  const roles = rawRoles ?? [];

  // Stats
  const totalRoles = roles.length;
  const systemCount = roles.filter((r) => r.isSystem).length;
  const customCount = roles.filter((r) => !r.isSystem).length;
  const totalPermissions = roles.reduce((s, r) => s + r._count.permissions, 0);
  const maxPerms = Math.max(...roles.map((r) => r._count.permissions), 1);

  // Filter + sort
  const filtered = sortRoles(
    roles.filter((r) => {
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        if (!r.name.toLowerCase().includes(q) && !(r.description ?? '').toLowerCase().includes(q))
          return false;
      }
      if (typeFilter === 'system' && !r.isSystem) return false;
      if (typeFilter === 'custom' && r.isSystem) return false;
      if (authorityFilter !== '__all__' && !r.licensingAuthorities?.includes(authorityFilter))
        return false;
      return true;
    }),
    sort,
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader
        title="Roles"
        description="Manage roles and permission sets for your organization."
        actions={
          can('role:create') ? (
            <Button variant="success" onClick={() => navigate({ to: '/users/roles/new' })}>
              <Plus size={16} />
              Create New Role
            </Button>
          ) : null
        }
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Shield size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          delta={`${systemCount} system · ${customCount} custom`}
          value={totalRoles}
          label="Total Roles"
          loading={isLoading}
        />
        <StatCard
          icon={<Lock size={18} />}
          iconBg="bg-violet-50"
          iconColor="text-violet-500"
          delta="Protected by default"
          value={systemCount}
          label="System Roles"
          loading={isLoading}
        />
        <StatCard
          icon={<Sparkles size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta="Fully configurable"
          value={customCount}
          label="Custom Roles"
          loading={isLoading}
        />
        <StatCard
          icon={<Zap size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          delta="Across all roles"
          value={totalPermissions}
          label="Total Permissions"
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        {/* Search */}
        <div className="relative min-w-60 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles by name or description..."
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>

        {/* Type filter pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(['all', 'system', 'custom'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setTypeFilter(v)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                typeFilter === v
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {v === 'all' ? 'All Types' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Licensing Authority dropdown — hidden for single-authority actors, who only ever see their own municipality anyway */}
        {showAuthorityFilter && (
          <Select value={authorityFilter} onValueChange={setAuthorityFilter}>
            <SelectTrigger className="h-9 w-48 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTHORITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Sort dropdown */}
        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="h-9 w-44 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Count */}
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {totalRoles} roles
        </span>
      </div>

      {/* Role list */}
      <CardDataTable
        gridTemplateColumns={ROLE_GRID}
        columns={ROLE_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load roles</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <Shield size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No roles found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your search or filters</p>
          </div>
        }
        rows={paginated.map(
          (role): CardDataTableRowDef => ({
            id: role.id,
            avatar: <GradientAvatar name={role.name} size="md" shape="circle" />,
            cells: {
              role: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors leading-snug">
                    {role.name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-gray-400">{role.description ?? '—'}</p>
                  {role.modules.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      {role.modules.slice(0, 2).map((m) => (
                        <ModuleTag key={m} resource={m} />
                      ))}
                      {role.modules.length > 2 && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                          +{role.modules.length - 2} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ),
              permissions: (
                <div className="min-w-0">
                  <div className="flex items-baseline gap-1.5 mb-1.5">
                    <span className="text-sm font-bold text-gray-900">
                      {role._count.permissions}
                    </span>
                    <span className="text-xs text-gray-400">perms</span>
                  </div>
                  <ProgressBar
                    value={role._count.permissions}
                    max={maxPerms}
                    color="bg-success-500"
                    className="w-full max-w-[140px]"
                  />
                </div>
              ),
              users: (
                <div className="flex items-center gap-1.5">
                  <Users size={13} className="shrink-0 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{role._count.users}</span>
                  <span className="text-xs text-gray-400">users</span>
                </div>
              ),
              type: role.isSystem ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500">
                  <Lock size={11} />
                  System
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                  <Settings2 size={11} />
                  Custom
                </span>
              ),
            },
            onClick: () => navigate({ to: '/users/roles/$roleId', params: { roleId: role.id } }),
          }),
        )}
      />

      {/* Pagination footer */}
      {!isLoading && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {paginated.length} of {filtered.length} roles
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
